import { useQuery } from '@tanstack/react-query';

import { kbUsageRollup } from '@/api/kb';
import { formatUsdCost } from '@/lib/kb-cost';

import { useAuth } from '../auth/AuthContext';

/** Human labels for the KB surfaces (design §7.1-7.3). Unknown surfaces fall
 *  back to the raw key so a new surface still renders. */
const SURFACE_LABELS: Record<string, string> = {
  ask: 'Ask',
  explain: 'Explain',
  suggest: 'Suggest',
};

/** Stable query key for the rollup — callers invalidate this prefix after a KB
 *  call so the widget refetches (see `ask-kb.tsx`). Exported so the trigger and
 *  the reader can't drift apart. */
export const KB_USAGE_ROLLUP_KEY = ['kb-usage-rollup'] as const;

/** KB cost rollup (design §7.4) — cumulative spend + a per-surface breakdown,
 *  read from the `kb_usage` ledger. Keeps LLM spend visible where the work
 *  happens rather than buried in an AWS bill. Renders nothing until it loads.
 *
 *  Refetch is driven by cache invalidation of `KB_USAGE_ROLLUP_KEY`, not by a
 *  prop — a KB response carries no nonce, so keying off the answer payload would
 *  miss a repeated (still-billed) call that returns an identical answer. */
export function KbCostWidget() {
  const { isSignedIn, userKey, getIdToken } = useAuth();

  const rollupQuery = useQuery({
    queryKey: [...KB_USAGE_ROLLUP_KEY, userKey],
    enabled: isSignedIn,
    queryFn: async () => kbUsageRollup((await getIdToken()) ?? ''),
  });

  const rollup = rollupQuery.data;
  if (!rollup) return null;

  return (
    <div className="shrink-0 rounded-md border border-border px-3 py-2 text-right">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">KB spend</div>
      <div className="font-mono text-sm tabular-nums text-brand-navy">
        {formatUsdCost(rollup.cost_usd)}
      </div>
      <div className="text-xs text-muted-foreground">{rollup.calls} calls</div>

      {rollup.by_surface.length > 0 ? (
        <ul className="mt-2 grid gap-0.5 border-t border-border pt-2">
          {rollup.by_surface.map((s) => (
            <li
              key={s.surface}
              className="flex items-center justify-end gap-2 text-xs text-muted-foreground"
            >
              <span>{SURFACE_LABELS[s.surface] ?? s.surface}</span>
              <span className="font-mono tabular-nums text-brand-slate">
                {formatUsdCost(s.cost_usd)}
              </span>
              <span className="tabular-nums">({s.calls})</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
